// The audit-trail SLOT — how ANY module's detail screen shows a record's history
// without importing @erp/module-audit.
//
// WHY THIS EXISTS: detail screens across LIB (book, copy, patron, publisher,
// author, ebook, purchase order, serial) and FIN (invoice) mount audit's
// <RecordHistory/>. While those screens lived in apps/portals/admin that was
// legal — a portal may import any module. Inside a module it is a
// `module → module` edge, which
// ADR-001 Fig 4.1 bans outright: it fuses the LIB chunk with the AUDIT chunk and
// makes LIB unportable to any deployment that does not run audit.
//
// So LIB declares the HOLE and the shell fills it. The portal — the one place
// both modules legally meet (Law F1: "apps are shells") — provides audit's real
// component; anywhere it is not provided the default renders nothing, which is
// the honest behaviour for a deployment with no audit module rather than a
// crash. This is the same seam shape as `fe/scan`'s onScan funnel: the module
// owns the call site, never the transport.
//
// Promoted from modules/lib to @erp/ui (DEV-GUIDE §7.8) when FIN's InvoiceDetail
// needed the same hole: a module importing the slot from another module would be
// the very edge the slot exists to avoid. It names no domain — `entity` and
// `entityId` are the trail's own vocabulary — which is the test for living here.
import { createContext, useContext, type JSX } from "solid-js";

export interface RecordHistorySlotProps {
  /** The entity TYPE as the trail stores it — the singular route noun ('book', 'patron'). */
  entity: string;
  /** The record's opaque id. */
  entityId: string;
  /** Denser type and no Card chrome — for a sidebar or a tab body that already has a surface. */
  compact?: boolean;
  /** Rows per page (keyset). */
  pageSize?: number;
  class?: string;
}

/** Structurally exactly @erp/module-audit/fe's RecordHistoryProps, by contract not by import. */
export type RecordHistorySlot = (props: RecordHistorySlotProps) => JSX.Element;

/** Renders nothing: a deployment without an audit module shows no trail, not an error. */
const RecordHistoryContext = createContext<RecordHistorySlot>(() => null);

/** The shell wraps its routes in this, passing @erp/module-audit/fe's RecordHistory. */
export const RecordHistoryProvider = RecordHistoryContext.Provider;

/** Call in a component body; render the result as `<History entity=… entityId=… />`. */
export const useRecordHistory = () => useContext(RecordHistoryContext);
